import React, { useState, useEffect, useRef } from "react";
import { Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../redux/slices/authSlice";
import HttpApis from "../../services/HttpApis";
import { isPlausibleEmail as validateEmail } from "../../utils/validateEmail";
import Auth from "../../services/auth";
import { Notyf } from "notyf";
import "notyf/notyf.min.css";
import * as Styled from "../Login/style";
import { useTenant } from "../../TenantContext";

// Signup email verification. Mirrors the ForgotPassword OTP screen, but keeps
// its own localStorage keys — sharing `otpExpiryTime` with the password-reset
// flow would make one timer clobber the other.
const RESEND_EXPIRY_KEY = "verifyEmailResendExpiry";
const PENDING_EMAIL_KEY = "pendingVerificationEmail";
const RESEND_COOLDOWN_SECONDS = 300;

const VerifyEmail = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [timer, setTimer] = useState(RESEND_COOLDOWN_SECONDS);
  const [canResend, setCanResend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const timerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { logo } = useTenant(); // tenant logo, else env/bundled fallback
  const notyf = new Notyf({ position: { x: "center", y: "center" } });


  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins} min ${secs} sec` : `${secs} sec`;
  };

  const calculateRemainingTime = () => {
    const expiryTime = localStorage.getItem(RESEND_EXPIRY_KEY);
    if (!expiryTime) return 0;
    const remaining = Math.round((parseInt(expiryTime, 10) - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  };

  const startTimer = (secondsDuration = RESEND_COOLDOWN_SECONDS) => {
    if (timerRef.current) clearInterval(timerRef.current);
    let remaining = calculateRemainingTime();
    if (remaining <= 0) {
      localStorage.setItem(RESEND_EXPIRY_KEY, String(Date.now() + secondsDuration * 1000));
      remaining = secondsDuration;
    }

    setTimer(remaining);
    setCanResend(false);

    timerRef.current = setInterval(() => {
      const timeLeft = calculateRemainingTime();
      if (timeLeft <= 0) {
        clearInterval(timerRef.current);
        setTimer(0);
        setCanResend(true);
        localStorage.removeItem(RESEND_EXPIRY_KEY);
      } else {
        setTimer(timeLeft);
      }
    }, 1000);
  };

  useEffect(() => {
    const storedEmail = location.state?.email || localStorage.getItem(PENDING_EMAIL_KEY);
    if (storedEmail) {
      setEmail(storedEmail);
      localStorage.setItem(PENDING_EMAIL_KEY, storedEmail);
    } else {
      // No address to verify against — nothing useful to show here.
      navigate("/login", { replace: true });
      return;
    }
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const readError = (err) => {
    if (err?.errors) return Object.values(err.errors).flat().join(", ");
    if (err?.response?.data?.errors) {
      return Object.values(err.response.data.errors).flat().join(", ");
    }
    if (err?.response?.data?.message) return err.response.data.message;
    if (err?.message) return err.message;
    return "An error occurred. Please try again.";
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") handleVerify();
  };

  const handleVerify = async () => {
    setError("");
    setMessage("");

    if (!email || !validateEmail(email)) {
      setError("A valid email is required");
      return;
    }
    if (!otp) {
      setError("Enter the verification code from your email");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("email", email);
      formData.append("otp", otp);

      const response = await HttpApis.verifyEmail(formData);

      if (response.status === 200) {
        localStorage.removeItem(RESEND_EXPIRY_KEY);
        localStorage.removeItem(PENDING_EMAIL_KEY);

        // A correct code logs the user in: the backend returns a full login
        // payload (token + user + subscription) alongside the verification
        // result, so signup flows straight through to the app.
        const {
          token,
          refresh_token,
          expire_date,
          user: userData,
          subscription,
        } = response.data?.data || {};

        if (token && userData) {
          localStorage.setItem(
            "userId",
            userData.id || userData.profile?.id || "anonymous"
          );
          Auth.setToken({ token, refresh_token, expire_date });
          Auth.startAutoRefresh(HttpApis.refreshToken);

          if (userData.is_premium && subscription) {
            localStorage.setItem("subscriptionData", JSON.stringify(subscription));
            localStorage.setItem("subscriptionId", subscription.id);
          }

          dispatch(
            loginSuccess({
              user: { data: { user: userData, token } },
              token,
            })
          );

          notyf.success("Email verified! You're all set.");
          navigate("/profile", { replace: true });
          return;
        }

        // No session came back — the "already verified" response carries no
        // token by design, and session issuance can fail after the address is
        // committed. Either way verification succeeded; fall back to signing in.
        // (Auth.isAuth() is true whenever authData exists, so check the token
        // itself: an already-signed-in user keeps their session.)
        if (Auth.getToken()?.token) {
          notyf.success("Email verified successfully!");
          navigate("/profile", { replace: true });
        } else {
          notyf.success("Email verified! Please sign in to continue.");
          navigate("/login", { state: { email }, replace: true });
        }
      } else {
        setError("Failed to verify email");
      }
    } catch (err) {
      setError(readError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setMessage("");

    if (!email || !validateEmail(email)) {
      setError("A valid email is required");
      return;
    }

    try {
      setResending(true);
      const formData = new FormData();
      formData.append("email", email);

      const response = await HttpApis.resendVerification(formData);
      if (response.status === 200) {
        notyf.success("A new verification code has been sent to your email");
        setMessage("A new code is on its way.");
        setOtp("");
        localStorage.removeItem(RESEND_EXPIRY_KEY);
        startTimer(RESEND_COOLDOWN_SECONDS);
      } else {
        setError("Failed to resend the verification code");
      }
    } catch (err) {
      setError(readError(err));
      // Server-side cooldown rejected us — restart the local timer so the
      // button doesn't invite another immediate retry.
      startTimer(RESEND_COOLDOWN_SECONDS);
    } finally {
      setResending(false);
    }
  };

  return (
    <Styled.LoginContainer>
      <Styled.MainContainer className="container">
        <div className="logo-container">
          <img
            src={logo}
            alt="Logo"
            className="logo"
          />
        </div>
        <div className="formContainer">
          <p className="para">Verify your email</p>
          <p
            className="paraText"
            style={{ marginBottom: "14px", opacity: 0.8, fontSize: "14px", textAlign: "center" }}
          >
            We sent a 6-digit code to{" "}
            <strong style={{ color: "var(--brand, #B87333)" }}>{email}</strong>.
            <br />
            Enter it below to finish setting up your account.
          </p>
          <input
            placeholder="Enter your email"
            className="inputBox"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={true}
            style={{ opacity: 0.6, cursor: "not-allowed" }}
          />
          <input
            placeholder="Enter verification code"
            className="inputBox"
            value={otp}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            onChange={(e) => {
              setOtp(e.target.value.replace(/\D/g, ""));
              setError("");
            }}
            onKeyDown={handleKeyDown}
          />
          {error && <p className="errorText">{error}</p>}
          {message && <p className="successText">{message}</p>}

          <div className="resend-container">
            {canResend ? (
              <span
                className="resend-active"
                onClick={resending ? undefined : handleResend}
                style={resending ? { opacity: 0.6, cursor: "progress" } : undefined}
              >
                {resending ? "Sending…" : "Resend code"}
              </span>
            ) : (
              <span className="resend-timer">
                Resend code in <span>{formatTimer(timer)}</span>
              </span>
            )}
          </div>

          <Button
            className="btn"
            onClick={handleVerify}
            disabled={loading}
            style={{
              backgroundColor: "var(--brand, #B87333)",
              borderColor: "var(--brand, #B87333)",
            }}
          >
            {loading ? "Verifying…" : "Verify code"}
          </Button>
        </div>
      </Styled.MainContainer>
    </Styled.LoginContainer>
  );
};

export default VerifyEmail;
